/**
 * UserTimeDialog - View and edit time entries for a specific user on a lead
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getLeadTimeEntries,
  updateLeadTimeEntry,
  deleteLeadTimeEntry,
  deleteLeadTimeEntries,
} from '../../utils/leadTimeTrackingService';
import {
  buildLeadTimeDisplayGroups,
  sumMinutesInGroup,
  type LeadTimeDisplayGroup,
} from '../../utils/leadTimeDisplayGroups';
import type { LeadTimeEntry } from '../../types';
import { X, Trash2, ChevronDown } from 'lucide-react';
import './UserTimeDialog.css';

interface UserTimeDialogProps {
  leadId: string;
  userId: string;
  userName: string;
  onClose: () => void;
  onSaved?: () => void;
}

function formatDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function formatMinutes(minutes: number): string {
  const rounded = Math.round(minutes * 10) / 10;
  if (rounded < 60) return `${rounded}m`;
  const h = Math.floor(rounded / 60);
  const m = Math.round(rounded % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function groupKey(g: LeadTimeDisplayGroup): string {
  if (g.kind === 'single') return g.entry.id ?? '';
  return g.entries.map(e => e.id).join('|');
}

export default function UserTimeDialog({
  leadId,
  userId,
  userName,
  onClose,
  onSaved,
}: UserTimeDialogProps) {
  const [entries, setEntries] = useState<LeadTimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editMinutes, setEditMinutes] = useState(0);
  const [editDate, setEditDate] = useState('');
  const [editNote, setEditNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteProgress, setDeleteProgress] = useState<{ done: number; total: number } | null>(null);
  const [expandedMergedKey, setExpandedMergedKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const displayGroups = useMemo(() => buildLeadTimeDisplayGroups(entries), [entries]);

  const load = useCallback(async () => {
    setLoading(true);
    setActionError(null);
    try {
      const all = await getLeadTimeEntries(leadId);
      setEntries(all.filter(e => e.userId === userId));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not load time entries';
      setActionError(msg);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [leadId, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const startEdit = (e: LeadTimeEntry) => {
    if (!e.id) return;
    setEditingId(e.id);
    setEditMinutes(Math.round(e.minutes * 10) / 10);
    setEditDate(formatDate(e.startTime));
    setEditNote(e.note ?? '');
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const saveEdit = async () => {
    if (!editingId || saving) return;
    setSaving(true);
    setActionError(null);
    try {
      const startTime = new Date(editDate + 'T00:00:00');
      const endTime = new Date(startTime.getTime() + editMinutes * 60 * 1000);
      await updateLeadTimeEntry(editingId, { minutes: editMinutes, startTime, endTime, note: editNote || undefined });
      await load();
      setEditingId(null);
      onSaved?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not save time entry';
      setActionError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (deletingId) return;
    if (!id?.trim()) {
      setActionError('Invalid entry — try closing and reopening this dialog.');
      return;
    }
    setDeletingId(id);
    setActionError(null);
    try {
      await deleteLeadTimeEntry(id);
      await load();
      setEditingId(null);
      onSaved?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not delete time entry';
      setActionError(msg);
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteMany = async (ids: string[]) => {
    if (deletingId) return;
    const clean = ids.filter(Boolean);
    if (clean.length === 0) {
      setActionError('Nothing to delete.');
      return;
    }
    const label =
      clean.length === 1
        ? 'Delete this time entry?'
        : `Delete ${clean.length.toLocaleString()} auto-tracked segments? This cannot be undone.`;
    if (!window.confirm(label)) return;

    setDeletingId('batch');
    setDeleteProgress({ done: 0, total: clean.length });
    setActionError(null);
    try {
      await deleteLeadTimeEntries(clean, (done, total) => setDeleteProgress({ done, total }));
      await load();
      setEditingId(null);
      setExpandedMergedKey(null);
      onSaved?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not delete time entries';
      setActionError(msg);
    } finally {
      setDeletingId(null);
      setDeleteProgress(null);
    }
  };

  const totalMinutes = entries.reduce((s, e) => s + e.minutes, 0);

  const renderEntryEdit = (_entry?: LeadTimeEntry) => (
    <div className="user-time-dialog-edit">
      <div className="user-time-dialog-field">
        <label>Date</label>
        <input type="date" value={editDate} onChange={ev => setEditDate(ev.target.value)} />
      </div>
      <div className="user-time-dialog-field">
        <label>Minutes</label>
        <input
          type="number"
          min={1}
          max={480}
          value={editMinutes}
          onChange={ev => setEditMinutes(Math.max(0, parseInt(ev.target.value, 10) || 0))}
        />
      </div>
      <div className="user-time-dialog-field">
        <label>Note</label>
        <input
          type="text"
          value={editNote}
          onChange={ev => setEditNote(ev.target.value)}
          placeholder="Optional"
        />
      </div>
      <div className="user-time-dialog-edit-actions">
        <button type="button" className="user-time-dialog-cancel" onClick={cancelEdit} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="user-time-dialog-submit" onClick={saveEdit} disabled={saving || editMinutes <= 0}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );

  const renderSingleRow = (e: LeadTimeEntry) => {
    if (editingId === e.id) {
      return (
        <li key={e.id!} className="user-time-dialog-entry">
          {renderEntryEdit(e)}
        </li>
      );
    }

    const isAuto = e.type === 'implied';
    return (
      <li key={e.id!} className="user-time-dialog-entry">
        <div className="user-time-dialog-entry-content" onClick={() => startEdit(e)}>
          <div className="user-time-dialog-entry-top">
            <div className="user-time-dialog-entry-main">
              <div className="user-time-dialog-entry-text">
                <span className="user-time-dialog-date">{formatDate(e.startTime)}</span>
                {isAuto && <span className="user-time-dialog-badge">Auto</span>}
                <div className="user-time-dialog-range-line">
                  <span className="user-time-dialog-range">
                    {formatTime(e.startTime)} – {formatTime(e.endTime)}
                  </span>
                  <span className="user-time-dialog-range-sep">·</span>
                  <span className="user-time-dialog-minutes">{formatMinutes(e.minutes)}</span>
                </div>
              </div>
            </div>
            <button
              type="button"
              className="user-time-dialog-delete"
              onClick={ev => {
                ev.stopPropagation();
                handleDelete(e.id!);
              }}
              disabled={deletingId === e.id}
              aria-label="Delete entry"
            >
              <Trash2 size={14} />
            </button>
          </div>
          {e.note && <div className="user-time-dialog-note">{e.note}</div>}
        </div>
      </li>
    );
  };

  const renderMergedRow = (g: Extract<LeadTimeDisplayGroup, { kind: 'merged' }>) => {
    const key = groupKey(g);
    const first = g.entries[0];
    const last = g.entries[g.entries.length - 1];
    const total = sumMinutesInGroup(g);
    const expanded = expandedMergedKey === key;
    const ids = g.entries.map(e => e.id!).filter(Boolean);

    return (
      <li key={key} className="user-time-dialog-entry user-time-dialog-entry--merged">
        <div className={`user-time-dialog-merged ${expanded ? 'user-time-dialog-merged--open' : ''}`}>
          <div className="user-time-dialog-entry-content user-time-dialog-entry-content--merged-head">
            <div className="user-time-dialog-entry-top">
              <button
                type="button"
                className="user-time-dialog-merge-toggle"
                onClick={() => setExpandedMergedKey(expanded ? null : key)}
                aria-expanded={expanded}
                aria-label={expanded ? 'Hide segments' : 'Show segments'}
              >
                <ChevronDown size={16} className={expanded ? 'user-time-dialog-chevron-open' : ''} />
              </button>
              <div className="user-time-dialog-entry-main">
                <div className="user-time-dialog-entry-text">
                  <span className="user-time-dialog-date">{formatDate(first.startTime)}</span>
                  <span className="user-time-dialog-badge">Auto</span>
                  <span className="user-time-dialog-merge-count">{g.entries.length} segments</span>
                  <div className="user-time-dialog-range-line">
                    <span className="user-time-dialog-range">
                      {formatTime(first.startTime)} – {formatTime(last.endTime)}
                    </span>
                    <span className="user-time-dialog-range-sep">·</span>
                    <span className="user-time-dialog-minutes">{formatMinutes(total)} total</span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="user-time-dialog-delete"
                onClick={ev => {
                  ev.stopPropagation();
                  void handleDeleteMany(ids);
                }}
                disabled={deletingId === 'batch'}
                aria-label="Delete all segments in this session"
                title={
                  ids.length > 500
                    ? `Delete ${ids.length.toLocaleString()} segments (may take a few seconds)`
                    : 'Delete all segments in this session'
                }
              >
                <Trash2 size={14} />
              </button>
            </div>
            <p className="user-time-dialog-merge-hint">Continuous work; combined from short auto-tracked saves.</p>
          </div>

          {expanded && (
            <ul className="user-time-dialog-merge-segments">
              {g.entries.map(e => (
                <li key={e.id} className="user-time-dialog-merge-segment">
                  {editingId === e.id ? (
                    renderEntryEdit(e)
                  ) : (
                    <>
                      <div
                        className="user-time-dialog-merge-segment-row"
                        onClick={() => startEdit(e)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={ev => {
                          if (ev.key === 'Enter' || ev.key === ' ') {
                            ev.preventDefault();
                            startEdit(e);
                          }
                        }}
                      >
                        <span className="user-time-dialog-merge-segment-time">
                          {formatTime(e.startTime)} – {formatTime(e.endTime)}
                        </span>
                        <span className="user-time-dialog-merge-segment-min">{formatMinutes(e.minutes)}</span>
                      </div>
                      <button
                        type="button"
                        className="user-time-dialog-delete user-time-dialog-delete--small"
                        onClick={ev => {
                          ev.stopPropagation();
                          handleDelete(e.id!);
                        }}
                        disabled={deletingId === e.id}
                        aria-label="Delete segment"
                      >
                        <Trash2 size={12} />
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </li>
    );
  };

  return (
    <div
      className="user-time-dialog-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="user-time-title"
    >
      <div className="user-time-dialog" onClick={e => e.stopPropagation()}>
        <div className="user-time-dialog-header">
          <h2 id="user-time-title">
            Time: {userName}
            {totalMinutes > 0 && (
              <span className="user-time-dialog-total">
                ({formatMinutes(totalMinutes)})
              </span>
            )}
          </h2>
          <button type="button" className="user-time-dialog-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="user-time-dialog-body">
          {actionError && (
            <div className="user-time-dialog-error" role="alert">
              {actionError}
            </div>
          )}
          {deleteProgress && (
            <div className="user-time-dialog-progress" role="status">
              Deleting {deleteProgress.done.toLocaleString()} / {deleteProgress.total.toLocaleString()}…
            </div>
          )}
          {loading ? (
            <div className="user-time-dialog-loading">Loading…</div>
          ) : entries.length === 0 ? (
            <div className="user-time-dialog-empty">No time entries yet</div>
          ) : (
            <ul className="user-time-dialog-list">
              {displayGroups.map(g =>
                g.kind === 'single' ? renderSingleRow(g.entry) : renderMergedRow(g),
              )}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
